import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '../../../../../auth0/reactAuth0Spa';
import { checkLoginForError, formatBearerToken } from '../../../../../Common.functions';
import { DepartmentContext } from '../../../../../DepartmentWrapper';
import { fetchDataAgnostic } from '../../../../../Services/dataApi';
import Loading from 'Body/Statuses/Loading';
import RankChartPrintGraph from './RankChartPrintGraph';
import RankChartPrintHeader from './RankChartPrintHeader';
import RankChartPrintLegend from './RankChartPrintLegend';
import { buildPrintChartData, getPrintCandidatesPerPage, splitPrintChartPages } from './rankChartPrintData';
import { useRankChartPrintStyles } from './rankChartPrintStyles';
import {
  getRankChartPrintLayout,
  RANK_CHART_PRINT_LAYOUT_OPTIONS,
  RANK_CHART_PRINT_PAPER_OPTIONS,
} from './rankChartPrintLayout';
import { getRequestedWeightIds } from '../rankChartPreview';
import './RankChartPrintPreview.css';

const RankChartPrintPreview = ({ mode, rawData = [] }) => {
  const [weights, setWeights] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [printLayoutId, setPrintLayoutId] = useState('landscape');
  const [printPaperId, setPrintPaperId] = useState('letter');
  const printLayout = useMemo(
    () => getRankChartPrintLayout(printLayoutId, printPaperId),
    [printLayoutId, printPaperId],
  );
  const dContext = useContext(DepartmentContext);
  const { getTokenSilently, loginWithRedirect } = useAuth0();
  const location = useLocation();
  const selectedWeightIds = useMemo(() => getRequestedWeightIds(location.search), [location.search]);

  useRankChartPrintStyles(printLayoutId, printPaperId);

  useEffect(() => {
    if (!dContext || !dContext.department || !dContext.season) {
      return undefined;
    }

    let isCurrent = true;
    setIsLoading(true);
    getTokenSilently()
      .then((token) =>
        fetchDataAgnostic(
          'department/season/weightCategories',
          { pk_Department: dContext.department.pk_Department, pk_Season: dContext.season.pk_Season },
          formatBearerToken(token),
        ),
      )
      .then((response) => {
        if (isCurrent) {
          setWeights(response.data || []);
        }
      })
      .catch((err) => {
        checkLoginForError({ err, loginWithRedirect });
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [dContext, getTokenSilently, loginWithRedirect]);

  const { activeKeys, rows } = useMemo(
    () => buildPrintChartData(rawData, weights, mode, selectedWeightIds),
    [mode, rawData, selectedWeightIds, weights],
  );
  const candidatesPerPage = useMemo(
    () => getPrintCandidatesPerPage(activeKeys.length, printLayoutId, printPaperId),
    [activeKeys.length, printLayoutId, printPaperId],
  );
  const pages = useMemo(() => splitPrintChartPages(rows, candidatesPerPage), [candidatesPerPage, rows]);
  const modeLabel = mode === 2 ? 'Prescreen' : 'Evaluation';

  if (isLoading) {
    return <Loading options={{ labelText: 'Preparing Rank Chart. . .' }} />;
  }

  return (
    <main className="rank-chart-print-preview">
      <div className="rank-chart-print-controls" style={{ width: printLayout.sheetWidth }}>
        <label htmlFor="rank-chart-print-layout">Print layout</label>
        <select
          id="rank-chart-print-layout"
          onChange={(event) => setPrintLayoutId(event.target.value)}
          value={printLayoutId}
        >
          {RANK_CHART_PRINT_LAYOUT_OPTIONS.map((layout) => (
            <option key={layout.id} value={layout.id}>
              {layout.label}
            </option>
          ))}
        </select>
        <label htmlFor="rank-chart-print-paper">Paper size</label>
        <select
          id="rank-chart-print-paper"
          onChange={(event) => setPrintPaperId(event.target.value)}
          value={printPaperId}
        >
          {RANK_CHART_PRINT_PAPER_OPTIONS.map((paper) => (
            <option key={paper.id} value={paper.id}>
              {paper.label}
            </option>
          ))}
        </select>
      </div>
      {pages.map((pageRows, index) => (
        <section
          className={`rank-chart-print-sheet${index < pages.length - 1 ? ' rank-chart-print-sheet--has-following' : ''}`}
          key={`rank-chart-page-${index + 1}`}
          style={{ width: printLayout.sheetWidth }}
        >
          <RankChartPrintHeader
            department={dContext.department}
            modeLabel={modeLabel}
            pageNumber={index + 1}
            season={dContext.season}
            totalPages={pages.length}
          />
          <RankChartPrintLegend keys={activeKeys} layoutId={printLayoutId} />
          {pageRows.length > 0 ? (
            <RankChartPrintGraph keys={activeKeys} layoutId={printLayoutId} paperId={printPaperId} rows={pageRows} />
          ) : (
            <div className="rank-chart-print-empty">No ranked candidates are available.</div>
          )}
        </section>
      ))}
    </main>
  );
};

export default RankChartPrintPreview;
